/**
 * AriaDemoPage — Demo navigabile delle 4 modalità Aria.
 *
 * Spec utente (definitiva):
 *  • FREE      — bolla in basso + bandierina statica con frase di presentazione.
 *                L'utente DEVE cliccare per aprire la chat. Niente popup automatico.
 *                Niente scroll lock.
 *
 *  • WELCOME   — popup scheda automatica dopo qualche secondo.
 *                Suggerimenti cliccabili → aprono la chat completa.
 *                Niente scroll lock (la scheda non è fullscreen).
 *
 *  • FULL      — layer fullscreen scuro centrale + chat ambient.
 *                Scroll body LOCKED finché il layer è aperto.
 *
 *  • CINEMATIC — particle face Three.js (riusata da /promo) IN ALTO
 *                + chat in layer SOTTO. Scroll body LOCKED.
 *                Aria "vede" i contenuti del sito (prodotti pre-iniettati).
 *
 * Regole comuni:
 *  - Quando un layer/popup/scheda è aperto, lo SCROLL INTERNO DEL CONTENITORE
 *    deve funzionare senza strabordare fuori schermo (mobile-first).
 *  - Chat reale connessa a /api/hub/ai-guide (no stub).
 *  - Transizione silenziosa tra modalità (no toast tecnici).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Send, MessageCircle, X, MapPin, Phone, Clock, Star, Volume2 } from "lucide-react";
import { api } from "@/lib/api";
import AriaParticleFace from "@/components/AriaParticleFace";
import { AriaPresenceTrigger } from "@/pages/TotalKlickHub";

/* ============================================================
 * Demo contexts — sito finto per ogni modalità
 * ============================================================ */
const DEMO_CONTEXTS = {
    free: {
        brand: "Falegnameria Romeo",
        tagline: "Restauro mobili d'epoca · Vercelli",
        sector: "Artigianato",
        accent: "#4F8BFF", accent2: "#2B5AFF",
        glow: "rgba(79,139,255,0.35)",
        bg: "linear-gradient(180deg, #f5f1ea 0%, #ebe3d5 100%)",
        text: "#2A2218", sub: "#5C5042",
        plan_label: "Aria Free", plan_price: "€0 / per sempre",
        plan_blurb: "Bolla discreta in basso. Aria aspetta che tu la chiami.",
        hero_title: "Diamo nuova vita\nal legno di famiglia.",
        hero_sub: "Restauro artigianale dal 1962. Quattro generazioni di mani pazienti.",
        banner_phrase: "Ciao 👋 ti aiuto con qualcosa?",
        sections: [
            { title: "Cosa facciamo", body: "Restauro mobili antichi, lucidatura a tampone, sostituzione ferramenta originale." },
            { title: "Dove siamo", body: "Via dei Tigli 14, Vercelli — laboratorio aperto sabato mattina su appuntamento." },
        ],
        products: [],  // free non offre prodotti contestuali
    },
    welcome: {
        brand: "Trattoria Da Aria",
        tagline: "Cucina di montagna · Bardonecchia",
        sector: "Ristorazione",
        accent: "#86EFAC", accent2: "#10B981",
        glow: "rgba(134,239,172,0.4)",
        bg: "linear-gradient(180deg, #0E1F18 0%, #1A3A2A 100%)",
        text: "#FFFFFF", sub: "rgba(255,255,255,0.7)",
        plan_label: "Welcome Layer", plan_price: "€19 / mese",
        plan_blurb: "Una scheda gentile che accoglie l'utente prima che se ne vada.",
        hero_title: "La montagna nel piatto.\nIl fuoco vero in cucina.",
        hero_sub: "Pizzoccheri, polenta concia, brasato al Barolo. Cinque tavoli, prenotazione obbligatoria.",
        banner_phrase: "Buonasera 👋 vuoi un consiglio per stasera?",
        sections: [
            { title: "Il menu della settimana", body: "Cambia ogni 7 giorni. Solo prodotti di valle, niente compromessi." },
            { title: "Prenota", body: "Aperti giovedì-domenica, cena dalle 19:30. Posti limitati, meglio chiamare." },
        ],
        suggestions: ["Voglio prenotare", "Vedi il menu", "Dove siete esattamente?"],
        products: [
            { title: "Pizzoccheri", desc: "Grano saraceno, verza, formaggio Bitto", price: "€14" },
            { title: "Brasato al Barolo", desc: "Cottura 6h, polenta concia", price: "€22" },
        ],
    },
    full: {
        brand: "Vela Boutique Hotel",
        tagline: "Esperienza di soggiorno · Costa Smeralda",
        sector: "Ospitalità premium",
        accent: "#A855F7", accent2: "#7C3AED",
        glow: "rgba(168,85,247,0.4)",
        bg: "linear-gradient(180deg, #0B0820 0%, #1E1340 100%)",
        text: "#FFFFFF", sub: "rgba(255,255,255,0.72)",
        plan_label: "Full Experience", plan_price: "€49 / mese",
        plan_blurb: "Aria appare come layer: ti prende per mano e ti accompagna.",
        hero_title: "Dodici camere.\nUn mare che ti riconosce.",
        hero_sub: "Affacci diretti sul Golfo di Pevero. Concierge dedicato. Yacht charter su richiesta.",
        banner_phrase: "Benvenuto al Vela.",
        sections: [
            { title: "Le suite", body: "Da 45 a 110 mq. Vasca esterna, terrazza privata, tessuti sardi tessuti a mano." },
            { title: "Esperienze", body: "Snorkeling al Capo Coda Cavallo, cena su barca, tour vigne Vermentino." },
        ],
        suggestions: ["Mostrami le suite", "C'è il ristorante?", "Posso noleggiare uno yacht?"],
        products: [
            { title: "Suite Vela", desc: "85mq, terrazza vista mare, vasca esterna", price: "da €680/notte" },
            { title: "Suite Pevero", desc: "110mq, piscina privata, butler 24h", price: "da €1.200/notte" },
        ],
    },
    cinematic: {
        brand: "Studio Visione",
        tagline: "Agenzia creativa · Milano",
        sector: "Branding luxury",
        accent: "#FF1A5C", accent2: "#A855F7",
        glow: "rgba(255,26,92,0.4)",
        bg: "linear-gradient(180deg, #050010 0%, #15001F 100%)",
        text: "#FFFFFF", sub: "rgba(255,255,255,0.65)",
        plan_label: "Cinematic Experience", plan_price: "€99 / mese",
        plan_blurb: "Il volto di Aria che già conosci dal nostro promo. Voce vera. Presenza vera.",
        hero_title: "Brand che non si dimenticano.\nNeanche con gli occhi chiusi.",
        hero_sub: "Identità visive per maison del lusso, hospitality, automotive. Tre award Cannes Lions in cinque anni.",
        banner_phrase: "Sono Aria. Ti sto guardando.",
        sections: [
            { title: "Cosa facciamo", body: "Brand strategy, identità visiva, art direction, campagne. Niente template, niente compromessi." },
            { title: "Clienti", body: "Maison di moda, hospitality 5★, gruppi automotive premium. Selezioniamo i progetti." },
        ],
        suggestions: ["Mostrami i progetti", "Quanto costa una brand identity?", "Lavorate con startup?"],
        products: [
            { title: "Brand Identity Premium", desc: "Naming, logo, palette, manuale brand", price: "da €18.000" },
            { title: "Campagna 360°", desc: "Strategy + creatività + produzione + media planning", price: "da €45.000" },
        ],
    },
};

const VALID_MODES = ["free", "welcome", "full", "cinematic"];

/* ============================================================
 * Custom hook: scroll lock body
 * ============================================================ */
function useBodyScrollLock(locked) {
    useEffect(() => {
        if (!locked) return undefined;
        const prev = document.body.style.overflow;
        const prevTouchAction = document.body.style.touchAction;
        document.body.style.overflow = "hidden";
        document.body.style.touchAction = "none";
        return () => {
            document.body.style.overflow = prev;
            document.body.style.touchAction = prevTouchAction;
        };
    }, [locked]);
}

/* ============================================================
 * Main component
 * ============================================================ */
export default function AriaDemoPage() {
    const { mode } = useParams();
    const ctx = DEMO_CONTEXTS[mode];

    useEffect(() => {
        if (ctx) document.title = `Demo Aria · ${ctx.plan_label} — TotalKlick`;
    }, [ctx]);

    if (!VALID_MODES.includes(mode)) {
        return <Navigate to="/aria" replace />;
    }

    return (
        <div
            className="relative min-h-screen overflow-x-hidden"
            style={{ background: ctx.bg, color: ctx.text, fontFamily: "Geist, Outfit, sans-serif" }}
            data-testid={`aria-demo-${mode}`}
        >
            <DemoBanner ctx={ctx} />
            <FakeSiteContent ctx={ctx} />

            {/* Aria layer per la modalità */}
            {mode === "free" && <AriaFreeMode ctx={ctx} />}
            {mode === "welcome" && <AriaWelcomeMode ctx={ctx} />}
            {mode === "full" && <AriaFullMode ctx={ctx} />}
            {mode === "cinematic" && <AriaCinematicMode ctx={ctx} />}

            <EcosystemCTA mode={mode} ctx={ctx} />
        </div>
    );
}

/* ============================================================
 * BANNER + SITO FINTO
 * ============================================================ */
function DemoBanner({ ctx }) {
    return (
        <div
            className="fixed top-0 left-0 right-0 z-[60] backdrop-blur-md"
            style={{ background: "rgba(5,5,15,0.88)", borderBottom: `1px solid ${ctx.accent}40` }}
            data-testid="demo-banner"
        >
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-2 sm:gap-3 justify-between">
                <Link
                    to="/aria"
                    className="inline-flex items-center gap-1.5 text-[11px] text-white/75 hover:text-white transition uppercase tracking-[0.2em] flex-shrink-0"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                    data-testid="demo-banner-back"
                >
                    <ArrowLeft size={11} /> <span className="hidden sm:inline">torna alle modalità</span><span className="sm:hidden">indietro</span>
                </Link>
                <div className="flex items-center gap-2 min-w-0">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full flex-shrink-0" style={{ background: `${ctx.accent}18`, border: `1px solid ${ctx.accent}55` }}>
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: ctx.accent }} />
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/85 whitespace-nowrap" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            demo · {ctx.plan_label.toLowerCase()}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FakeSiteContent({ ctx }) {
    return (
        <>
            <header className="relative z-10 pt-14 px-4 sm:px-8">
                <div className="max-w-6xl mx-auto flex items-center justify-between py-5">
                    <div className="min-w-0">
                        <div className="text-[16px] sm:text-[20px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 600, letterSpacing: "-0.02em" }}>
                            {ctx.brand}
                        </div>
                        <div className="text-[9.5px] sm:text-[11px] opacity-60 uppercase tracking-[0.18em] truncate" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            {ctx.tagline}
                        </div>
                    </div>
                    <nav className="hidden md:flex items-center gap-7 text-[13px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        <span className="opacity-70 cursor-default">Home</span>
                        <span className="opacity-70 cursor-default">Servizi</span>
                        <span className="opacity-70 cursor-default">Contatti</span>
                    </nav>
                </div>
            </header>

            <section className="relative px-4 sm:px-8 pt-6 sm:pt-10 pb-16">
                <div className="max-w-6xl mx-auto">
                    <div className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.28em] opacity-60 mb-3 sm:mb-4" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {ctx.sector}
                    </div>
                    <h1
                        className="whitespace-pre-line max-w-3xl"
                        style={{
                            fontFamily: "Geist, sans-serif", fontWeight: 200,
                            fontSize: "clamp(1.9rem, 5.5vw, 4.4rem)",
                            letterSpacing: "-0.045em", lineHeight: 1.04,
                        }}
                    >
                        {ctx.hero_title}
                    </h1>
                    <p className="mt-5 max-w-xl text-[14px] sm:text-[15.5px] leading-relaxed" style={{ color: ctx.sub, fontFamily: "Outfit, sans-serif" }}>
                        {ctx.hero_sub}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white text-[13px] sm:text-[13.5px] transition hover:scale-[1.02]"
                            style={{
                                background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                                fontFamily: "Geist, sans-serif", fontWeight: 500,
                                boxShadow: `0 18px 40px -14px ${ctx.glow}`,
                            }}
                        >
                            Richiedi info <ArrowRight size={13} />
                        </button>
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] sm:text-[13.5px] transition"
                            style={{
                                background: "rgba(0,0,0,0.05)",
                                border: `1px solid ${ctx.sub}30`,
                                color: ctx.text, fontFamily: "Geist, sans-serif",
                            }}
                        >
                            Scopri di più
                        </button>
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-8 pb-20">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-5">
                    {ctx.sections.map((s, i) => (
                        <div
                            key={i}
                            className="rounded-2xl p-5 sm:p-6"
                            style={{
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${ctx.sub}20`,
                                backdropFilter: "blur(10px)",
                            }}
                        >
                            <div className="text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2" style={{ fontFamily: "JetBrains Mono, monospace", color: ctx.accent }}>
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <h3 className="text-[18px] sm:text-[20px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                {s.title}
                            </h3>
                            <p className="text-[13.5px] sm:text-[14px] leading-relaxed" style={{ color: ctx.sub, fontFamily: "Outfit, sans-serif" }}>
                                {s.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Info contatti finte */}
            <section className="px-4 sm:px-8 pb-28">
                <div
                    className="max-w-6xl mx-auto rounded-2xl p-5 grid grid-cols-2 sm:flex sm:flex-wrap gap-4 sm:gap-6"
                    style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${ctx.sub}15` }}
                >
                    <InfoItem Icon={MapPin} label="Sede" value={ctx.tagline.split(" · ")[1] || "Italia"} color={ctx.sub} />
                    <InfoItem Icon={Phone} label="Tel" value="+39 011 123 4567" color={ctx.sub} />
                    <InfoItem Icon={Clock} label="Orari" value="Lun-Ven 9:00-18:30" color={ctx.sub} />
                    <InfoItem Icon={Star} label="Recensioni" value="4.9 · 287" color={ctx.sub} />
                </div>
            </section>
        </>
    );
}

function InfoItem({ Icon, label, value, color }) {
    return (
        <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg grid place-items-center flex-none" style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${color}25` }}>
                <Icon size={13} style={{ color }} />
            </div>
            <div className="min-w-0">
                <div className="text-[9.5px] uppercase tracking-[0.2em] opacity-55" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</div>
                <div className="text-[12px] sm:text-[13px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{value}</div>
            </div>
        </div>
    );
}

/* ============================================================
 * MODALITÀ 1 — FREE
 * Bolla in basso + bandierina statica. CLICK obbligatorio per aprire.
 * Niente scroll lock.
 * ============================================================ */
function AriaFreeMode({ ctx }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            {/* Bandierina statica sopra la bolla — non si chiude da sola, sparisce solo all'apertura */}
            <AnimatePresence>
                {!open && (
                    <motion.button
                        type="button"
                        onClick={() => setOpen(true)}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 }}
                        className="fixed bottom-[88px] right-4 sm:right-6 z-50 max-w-[260px] text-left rounded-2xl px-3.5 py-2.5 cursor-pointer"
                        style={{
                            background: "rgba(15,18,36,0.96)",
                            border: `1px solid ${ctx.accent}55`,
                            boxShadow: `0 14px 36px -12px ${ctx.glow}`,
                            backdropFilter: "blur(18px)",
                        }}
                        data-testid="aria-free-banner"
                    >
                        <div className="text-white text-[12.5px] leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {ctx.banner_phrase}
                        </div>
                        <div className="text-[10px] mt-1 uppercase tracking-[0.2em] opacity-70" style={{ color: ctx.accent, fontFamily: "JetBrains Mono, monospace" }}>
                            tocca per aprire
                        </div>
                        {/* Tail */}
                        <div className="absolute bottom-[-6px] right-6 w-3 h-3 rotate-45" style={{ background: "rgba(15,18,36,0.96)", borderRight: `1px solid ${ctx.accent}55`, borderBottom: `1px solid ${ctx.accent}55` }} />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Bolla sempre presente */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="fixed bottom-5 right-4 sm:right-6 z-50 w-14 h-14 rounded-full grid place-items-center transition hover:scale-[1.06]"
                style={{
                    background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                    boxShadow: `0 18px 40px -12px ${ctx.glow}, 0 0 0 1px rgba(255,255,255,0.08) inset`,
                }}
                data-testid="aria-free-bubble"
                aria-label="Apri chat Aria"
            >
                {open ? <X size={18} className="text-white" /> : <Sparkles size={18} className="text-white" />}
                {!open && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-[#0B0820] animate-pulse" style={{ background: "#86EFAC" }} />}
            </button>

            {/* Panel chat — Intercom style bottom-right */}
            <AnimatePresence>
                {open && (
                    <ChatPanel
                        ctx={ctx}
                        variant="dock"
                        onClose={() => setOpen(false)}
                        mode="free"
                    />
                )}
            </AnimatePresence>
        </>
    );
}

/* ============================================================
 * MODALITÀ 2 — WELCOME
 * Scheda popup automatica dopo qualche secondo. Suggerimenti cliccabili.
 * Niente scroll lock.
 * ============================================================ */
function AriaWelcomeMode({ ctx }) {
    const [popupOpen, setPopupOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [seed, setSeed] = useState("");  // messaggio di partenza scelto dall'utente

    useEffect(() => {
        const t = setTimeout(() => setPopupOpen(true), 3500);
        return () => clearTimeout(t);
    }, []);

    const startChatWith = (text) => {
        setSeed(text);
        setPopupOpen(false);
        setChatOpen(true);
    };

    return (
        <>
            <AnimatePresence>
                {popupOpen && !chatOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:w-[360px] rounded-3xl overflow-hidden flex flex-col"
                        style={{
                            background: "linear-gradient(180deg, rgba(15,18,36,0.97), rgba(10,20,15,0.97))",
                            border: `1px solid ${ctx.accent}45`,
                            boxShadow: `0 30px 70px -18px ${ctx.glow}`,
                            backdropFilter: "blur(24px)",
                            maxHeight: "calc(100vh - 100px)",
                        }}
                        data-testid="aria-welcome-popup"
                    >
                        <div className="absolute -top-12 -left-12 w-40 h-40 rounded-full pointer-events-none" style={{
                            background: `radial-gradient(circle, ${ctx.accent}55 0%, transparent 70%)`,
                            filter: "blur(28px)",
                        }} />
                        <button
                            type="button"
                            onClick={() => setPopupOpen(false)}
                            className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center text-white/55 hover:text-white transition z-10"
                            style={{ background: "rgba(255,255,255,0.06)" }}
                            data-testid="aria-welcome-close"
                            aria-label="Chiudi"
                        >
                            <X size={13} />
                        </button>
                        <div className="relative p-5 overflow-y-auto">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-full grid place-items-center relative" style={{
                                    background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                                    boxShadow: `0 0 28px -4px ${ctx.glow}`,
                                }}>
                                    <Sparkles size={16} className="text-white" />
                                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0E1F18]" style={{ background: ctx.accent }} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-white text-[14.5px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>Aria · {ctx.brand}</div>
                                    <div className="text-[10.5px] uppercase tracking-[0.22em]" style={{ color: ctx.accent, fontFamily: "JetBrains Mono, monospace" }}>online</div>
                                </div>
                            </div>
                            <p className="text-white text-[14.5px] leading-relaxed mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Ciao 👋 sono Aria.
                            </p>
                            <p className="text-white/75 text-[13.5px] leading-relaxed mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                                Hai trovato {ctx.brand}. Posso aiutarti a capire se siamo quello che cerchi?
                            </p>
                            <div className="space-y-1.5">
                                {(ctx.suggestions || []).map((q, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => startChatWith(q)}
                                        className="w-full text-left px-3.5 py-2.5 rounded-xl text-[13px] text-white/85 hover:text-white transition"
                                        style={{
                                            background: "rgba(255,255,255,0.04)",
                                            border: `1px solid ${ctx.accent}30`,
                                            fontFamily: "Outfit, sans-serif",
                                        }}
                                        data-testid={`aria-welcome-suggest-${i}`}
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => startChatWith("")}
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-white text-[13px] transition hover:scale-[1.01]"
                                style={{
                                    background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                                    fontFamily: "Geist, sans-serif", fontWeight: 500,
                                    boxShadow: `0 14px 30px -10px ${ctx.glow}`,
                                }}
                                data-testid="aria-welcome-cta"
                            >
                                <MessageCircle size={13} /> Inizia a chattare
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {chatOpen && (
                    <ChatPanel
                        ctx={ctx}
                        variant="dock"
                        seed={seed}
                        onClose={() => setChatOpen(false)}
                        mode="welcome"
                    />
                )}
            </AnimatePresence>
        </>
    );
}

/* ============================================================
 * MODALITÀ 3 — FULL
 * Layer fullscreen scuro + chat ambient. Scroll body LOCKED.
 * ============================================================ */
function AriaFullMode({ ctx }) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setOpen(true), 3000);
        return () => clearTimeout(t);
    }, []);
    useBodyScrollLock(open);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="fixed inset-0 z-[55] flex items-center justify-center px-4 py-4 sm:py-8 overflow-y-auto"
                    style={{
                        background: "radial-gradient(ellipse at center, rgba(15,5,35,0.85) 0%, rgba(0,0,0,0.95) 80%)",
                        backdropFilter: "blur(28px)",
                    }}
                    data-testid="aria-full-layer"
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                >
                    <ChatPanel
                        ctx={ctx}
                        variant="center"
                        onClose={() => setOpen(false)}
                        mode="full"
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/* ============================================================
 * MODALITÀ 4 — CINEMATIC
 * Riusa esattamente l'esperienza presenza della Home (AriaPresenceTrigger
 * con autoOpen). Identica per design — volto a particelle + "Ciao. Sono Aria."
 * + chat con voce ElevenLabs. Scroll body LOCKED (AriaPresenceTrigger
 * dispatcha già aria-presence-mounted, ma forziamo anche scroll lock qui).
 * ============================================================ */
function AriaCinematicMode() {
    useBodyScrollLock(true);
    return <AriaPresenceTrigger autoOpen />;
}

// Componente legacy non più usato — la particle face ora è messa inline col size="fill"
// per essere più grande e avvolgente. Lo lascio per riferimento futuro.
// function CinematicFaceContainer() { ... }

/* ============================================================
 * CHAT PANEL — gestisce backend reale + 3 varianti visive
 * ============================================================ */
function ChatPanel({ ctx, variant = "dock", seed = "", onClose, mode, withVoice, withProductCards }) {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: `Ciao 👋 questa è la demo visiva del **${ctx.plan_label}** su un finto sito (${ctx.brand}).\n\nQui ti mostro **come appare e si comporta** Aria — il flusso, le animazioni, l'eleganza.\n\nPer provare la mia **conversazione reale**, parla con me direttamente sul sito di TotalKlick 👇`,
            isIntro: true,
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [voicePlaying, setVoicePlaying] = useState(false);
    const listRef = useRef(null);
    const seededRef = useRef(false);

    const send = useCallback(async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || loading) return;
        setInput("");
        const newMsgs = [...messages, { role: "user", content: msg }];
        setMessages(newMsgs);
        setLoading(true);
        try {
            const res = await api.post("/hub/ai-guide", {
                session_id: sessionId,
                message: msg,
                history: newMsgs.slice(-12).map((m) => ({ role: m.role, content: m.content })),
                path: window.location.pathname,
            });
            const data = res.data;
            if (data.session_id) setSessionId(data.session_id);
            const replyMsg = { role: "assistant", content: data.reply || "..." };

            // Dopo 2+ turni utente in chat, mostra reminder "vai su TotalKlick"
            const userTurns = newMsgs.filter(m => m.role === "user").length;
            const shouldRemind = userTurns >= 2 && !newMsgs.some(m => m.role === "reminder");
            const shouldShowProducts = withProductCards && userTurns >= 1 && (ctx.products || []).length > 0;

            setMessages((m) => {
                const next = [...m, replyMsg];
                if (shouldShowProducts && !m.some(x => x.role === "products")) {
                    next.push({ role: "products", products: ctx.products });
                }
                if (shouldRemind) {
                    next.push({ role: "reminder" });
                }
                return next;
            });
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "Scusa, c'è stato un problema tecnico. Riprova tra un attimo?" }]);
        } finally {
            setLoading(false);
        }
    }, [input, loading, messages, sessionId, withProductCards, ctx.products]);

    // Auto-send seed message on mount (Welcome → click su suggerimento)
    useEffect(() => {
        if (seed && !seededRef.current) {
            seededRef.current = true;
            send(seed);
        }
    }, [seed, send]);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // Styling per variante
    const variantStyles = {
        dock: {
            wrapper: "fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:w-[400px] rounded-3xl overflow-hidden flex flex-col",
            style: {
                background: "linear-gradient(180deg, rgba(15,18,36,0.97), rgba(10,12,22,0.98))",
                border: `1px solid ${ctx.accent}45`,
                boxShadow: `0 30px 70px -18px ${ctx.glow}`,
                backdropFilter: "blur(24px)",
                height: "min(560px, calc(100vh - 100px))",
            },
        },
        center: {
            wrapper: "relative z-10 w-full max-w-[460px] rounded-3xl overflow-hidden flex flex-col",
            style: {
                background: "linear-gradient(180deg, rgba(20,18,46,0.96), rgba(10,8,30,0.98))",
                border: `1px solid ${ctx.accent}50`,
                boxShadow: `0 50px 100px -25px ${ctx.glow}`,
                backdropFilter: "blur(28px)",
                height: "min(620px, calc(100vh - 140px))",
            },
        },
        cinematic: {
            wrapper: "relative w-full max-w-[480px] rounded-3xl overflow-hidden flex flex-col",
            style: {
                background: "linear-gradient(180deg, rgba(25,0,30,0.78), rgba(5,0,15,0.94))",
                border: `1px solid ${ctx.accent}55`,
                boxShadow: `0 -20px 80px -10px ${ctx.glow}, 0 40px 100px -20px ${ctx.glow}`,
                backdropFilter: "blur(20px)",
            },
        },
    };
    const vs = variantStyles[variant] || variantStyles.dock;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className={vs.wrapper}
            style={vs.style}
            onClick={(e) => e.stopPropagation()}
            data-testid={`aria-chat-panel-${variant}`}
        >
            {/* HEADER */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/5 flex-none">
                <div className="w-9 h-9 rounded-full grid place-items-center relative flex-none" style={{
                    background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                    boxShadow: `0 0 18px -2px ${ctx.glow}`,
                }}>
                    <Sparkles size={13} className="text-white" />
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2" style={{ background: "#86EFAC", borderColor: "rgba(10,12,22,1)" }} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white text-[13.5px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                        Aria · {ctx.brand}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: ctx.accent, fontFamily: "JetBrains Mono, monospace" }}>
                        {mode} mode
                    </div>
                </div>
                {withVoice && (
                    <button
                        type="button"
                        onClick={() => setVoicePlaying(!voicePlaying)}
                        className="w-8 h-8 rounded-full grid place-items-center transition flex-none"
                        style={{
                            background: voicePlaying ? `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})` : "rgba(255,255,255,0.06)",
                            border: voicePlaying ? "none" : "1px solid rgba(255,255,255,0.1)",
                        }}
                        data-testid="aria-voice-toggle"
                        aria-label="Voce"
                    >
                        <Volume2 size={13} className="text-white" />
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    className="w-8 h-8 rounded-full grid place-items-center text-white/55 hover:text-white transition flex-none"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                    data-testid="aria-chat-close"
                    aria-label="Chiudi"
                >
                    <X size={13} />
                </button>
            </div>

            {/* MESSAGES — scroll interno */}
            <div ref={listRef} className="flex-1 overflow-y-auto overscroll-contain px-4 py-3.5 space-y-2.5" style={{ fontFamily: "Outfit, sans-serif", WebkitOverflowScrolling: "touch" }}>
                {messages.map((m, i) => (
                    <Message key={i} msg={m} ctx={ctx} onProductSend={(p) => send(`Mi interessa ${p.title}`)} />
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="px-3.5 py-2.5 rounded-2xl flex items-center gap-1.5" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: ctx.accent, animationDelay: "0ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: ctx.accent, animationDelay: "120ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: ctx.accent, animationDelay: "240ms" }} />
                        </div>
                    </div>
                )}
            </div>

            {/* INPUT */}
            <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-2 px-4 py-3 border-t border-white/5 flex-none"
            >
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Scrivi un messaggio…"
                    className="flex-1 bg-transparent text-white text-[14.5px] placeholder:text-white/35 focus:outline-none px-1"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                    disabled={loading}
                    data-testid="aria-chat-input"
                />
                <button
                    type="submit"
                    disabled={!input.trim() || loading}
                    className="w-9 h-9 rounded-full grid place-items-center flex-none transition disabled:opacity-40"
                    style={{ background: input.trim() ? `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})` : "rgba(255,255,255,0.06)" }}
                    data-testid="aria-chat-send"
                    aria-label="Invia"
                >
                    <Send size={14} className="text-white" />
                </button>
            </form>
        </motion.div>
    );
}

/* ============================================================
 * Message renderer (chat bubble + product cards + reminder)
 * ============================================================ */
function Message({ msg, ctx, onProductSend }) {
    if (msg.role === "products") {
        return (
            <div className="space-y-2">
                <div className="text-[10.5px] uppercase tracking-[0.24em] opacity-60" style={{ color: ctx.accent, fontFamily: "JetBrains Mono, monospace" }}>
                    Aria propone dal sito
                </div>
                <div className="space-y-2">
                    {msg.products.map((p, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onProductSend(p)}
                            className="w-full text-left rounded-2xl p-3 transition hover:scale-[1.01]"
                            style={{
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${ctx.accent}35`,
                            }}
                            data-testid={`aria-product-${i}`}
                        >
                            <div className="flex justify-between items-start gap-2">
                                <div className="min-w-0">
                                    <div className="text-white text-[13.5px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{p.title}</div>
                                    <div className="text-white/65 text-[12px] mt-0.5 line-clamp-2" style={{ fontFamily: "Outfit, sans-serif" }}>{p.desc}</div>
                                </div>
                                <div className="text-[11px] flex-none px-2 py-1 rounded-full whitespace-nowrap" style={{ background: `${ctx.accent}20`, color: ctx.accent, fontFamily: "JetBrains Mono, monospace" }}>
                                    {p.price}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        );
    }
    if (msg.role === "reminder") {
        return <TotalKlickReminderCard ctx={ctx} />;
    }
    if (msg.isIntro) {
        return <IntroBubble msg={msg} ctx={ctx} />;
    }
    const isUser = msg.role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                className="max-w-[88%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap"
                style={{
                    background: isUser
                        ? `linear-gradient(135deg, ${ctx.accent}30, ${ctx.accent2}25)`
                        : "rgba(255,255,255,0.05)",
                    color: "rgba(255,255,255,0.95)",
                    border: `1px solid ${isUser ? `${ctx.accent}45` : "rgba(255,255,255,0.06)"}`,
                }}
            >
                {msg.content}
            </div>
        </div>
    );
}

/**
 * Bubble di apertura — spiega la natura "demo visiva" + linka a TotalKlick
 * per provare la vera conversazione. Render markdown light (**bold**).
 */
function IntroBubble({ msg, ctx }) {
    const parts = msg.content.split(/(\*\*[^*]+\*\*)/g);
    return (
        <div className="space-y-2.5">
            <div className="flex justify-start">
                <div
                    className="max-w-[92%] px-3.5 py-3 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap"
                    style={{
                        background: "rgba(255,255,255,0.05)",
                        color: "rgba(255,255,255,0.95)",
                        border: "1px solid rgba(255,255,255,0.06)",
                    }}
                >
                    {parts.map((part, i) => {
                        if (part.startsWith("**") && part.endsWith("**")) {
                            return <strong key={i} style={{ color: ctx.accent, fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
                        }
                        return <span key={i}>{part}</span>;
                    })}
                </div>
            </div>
            <TotalKlickReminderCard ctx={ctx} compact />
        </div>
    );
}

/**
 * Card "Apri Aria su TotalKlick" — ricorda all'utente che il dialogo reale
 * può provarlo sul sito di TotalKlick.
 */
function TotalKlickReminderCard({ ctx, compact }) {
    return (
        <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-2xl p-3 transition hover:scale-[1.01] no-underline group"
            style={{
                background: `linear-gradient(135deg, ${ctx.accent}18, ${ctx.accent2}12)`,
                border: `1px solid ${ctx.accent}50`,
            }}
            data-testid="aria-totalklick-reminder"
        >
            <div className="w-9 h-9 rounded-full grid place-items-center flex-none" style={{
                background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                boxShadow: `0 0 18px -4px ${ctx.glow}`,
            }}>
                <Sparkles size={13} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-white text-[12.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                    Apri Aria su TotalKlick
                </div>
                <div className="text-white/65 text-[11px] mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {compact ? "Per provare il dialogo reale" : "Continua la conversazione con la vera Aria"}
                </div>
            </div>
            <ArrowRight size={14} className="text-white/75 group-hover:text-white group-hover:translate-x-0.5 transition flex-none" />
        </a>
    );
}

/* ============================================================
 * Ecosystem CTA finale
 * ============================================================ */
function EcosystemCTA({ mode, ctx }) {
    return (
        <section className="relative z-10 px-4 sm:px-8 pb-24 sm:pb-32 pt-8 sm:pt-12">
            <div className="max-w-4xl mx-auto rounded-3xl p-6 sm:p-10 relative overflow-hidden text-center" style={{
                background: "linear-gradient(180deg, rgba(15,18,36,0.7), rgba(10,12,22,0.85))",
                border: `1px solid ${ctx.accent}45`,
                backdropFilter: "blur(20px)",
                boxShadow: `0 40px 80px -20px ${ctx.glow}`,
            }}>
                <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none" style={{
                    background: `radial-gradient(circle, ${ctx.accent}33 0%, transparent 70%)`,
                    filter: "blur(40px)",
                }} />
                <div className="relative">
                    <div className="text-[10.5px] uppercase tracking-[0.28em] mb-3" style={{ color: ctx.accent, fontFamily: "JetBrains Mono, monospace" }}>
                        {ctx.plan_label} · {ctx.plan_price}
                    </div>
                    <h2 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.7rem, 4vw, 3rem)", letterSpacing: "-0.035em", lineHeight: 1.08 }}>
                        Entra anche tu nell&apos;ecosistema<br />
                        <span style={{
                            background: `linear-gradient(180deg, #FFFFFF 0%, ${ctx.accent} 70%, ${ctx.accent}55 100%)`,
                            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                        }}>
                            di attività interconnesse di Aria.
                        </span>
                    </h2>
                    <p className="text-white/65 mt-4 max-w-xl mx-auto text-[13.5px] sm:text-[14.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {ctx.plan_blurb}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center gap-3 justify-center mt-7">
                        <Link
                            to={`/aria/piani/${mode}`}
                            className="inline-flex items-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-full text-white text-[13.5px] sm:text-[14px] transition hover:scale-[1.03]"
                            style={{
                                background: `linear-gradient(135deg, ${ctx.accent}, ${ctx.accent2})`,
                                fontFamily: "Geist, sans-serif", fontWeight: 500,
                                boxShadow: `0 22px 50px -14px ${ctx.glow}`,
                            }}
                            data-testid={`aria-demo-cta-${mode}`}
                        >
                            <Sparkles size={13} /> Attiva {ctx.plan_label} <ArrowRight size={13} />
                        </Link>
                        <Link
                            to="/aria"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] text-white/75 hover:text-white transition"
                            style={{
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.12)",
                                fontFamily: "Geist, sans-serif",
                            }}
                            data-testid={`aria-demo-back-${mode}`}
                        >
                            Confronta le modalità
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
